import { generateAIScoreService } from "api";
import { ConfirmationModal, Loader } from "components";
import { getError } from "helpers";
import { useApiRequest, useToast } from "hooks";
import { useEffect, useMemo, useState } from "react";

interface Props {
  show: boolean;
  close: () => void;
  assessment: number;
  candidates: number[];
  buckets: string[];
}
const AIScoring: React.FC<Props> = ({
  show,
  candidates,
  close,
  assessment,
  buckets,
}) => {
  const [load, setLoad] = useState(false);

  // Hooks
  const { openToast } = useToast();

  // API Request Hooks
  const {
    run: runCalc,
    data: calcResponse,
    requestStatus: calcStatus,
    error: calcError,
  } = useApiRequest({});
  const {
    run: runGen,
    data: genResponse,
    requestStatus: genStatus,
    error: genError,
  } = useApiRequest({});

  const handleCalc = () =>
    runCalc(
      generateAIScoreService(
        {
          assessment,
          responses: candidates.length === 0 ? undefined : candidates,
          buckets: buckets.length === 0 ? undefined : buckets,
        },
        "calculate"
      )
    );
  const handleGenerate = () =>
    runGen(
      generateAIScoreService({
        assessment,
        responses: candidates.length === 0 ? undefined : candidates,
        callback_url: window.location.href,
        buckets: buckets.length === 0 ? undefined : buckets,
      })
    );

  useEffect(() => {
    if (show) handleCalc();
  }, [show, assessment, candidates]);

  const message = useMemo(() => {
    if (calcResponse?.status === 200) {
      return calcResponse.data?.message;
    } else if (calcError) {
      openToast({
        show: true,
        ...getError({
          error: calcError,
          text: "Unable to calculate auto-scoring cost, please try again later",
          heading: "Sorry",
        }),
        type: "error",
        timeOut: 5000,
      });
    }
  }, [calcResponse, calcError]);

  useMemo(() => {
    if (genResponse?.status === 200) {
      const data = genResponse.data.data.data;
      const link = data?.authorization_url;

      const heading = link
        ? genResponse.data.message ??
          "We have received your request to generate AI scores"
        : genResponse.data.data.message ?? "Failed to initiate";
      const text = link ? "Redirecting to payment in 3...2...1" : "";

      openToast({
        show: true,
        heading,
        text,
        type: link ? "success" : "error",
        timeOut: 3500,
      });

      if (link) {
        setLoad(true);
        setTimeout(() => {
          window.location.assign(link);
        }, 2000);
      }
    } else if (genError) {
      openToast({
        show: true,
        ...getError({
          error: genError,
          text: "Unable to initiate auto-scoring, please try again later",
          heading: "Sorry",
        }),
        type: "error",
        timeOut: 5000,
      });
    }
  }, [genResponse, genError]);

  const showLoader = calcStatus.isPending || genStatus.isPending || load;

  return (
    <>
      <Loader loading={showLoader} />
      {calcResponse && (
        <ConfirmationModal
          show={show}
          close={close}
          handleContinue={handleGenerate}
          title={"Generate AI Scoring"}
          text={
            <>
              Are you sure you want to continue? {message}.
              <br />
              <br />
              <i>
                Please note that the auto-generated scores will be available
                after 24 hours.
              </i>
            </>
          }
        />
      )}
    </>
  );
};

export { AIScoring };
